import * as Astronomy from 'astronomy-engine';
import { ayanamsaDeg, normalize360, timeFromJD } from './ayanamsa.ts';

/**
 * Sidereal (Lahiri) longitudes of the nine grahas, plus the Ascendant.
 *
 * Four conventions are fixed here and disclosed in the UI, because they are
 * exactly where free astrology sites silently disagree with each other:
 *
 *  1. Ayanamsa — Lahiri / Chitrapaksha (see ayanamsa.ts).
 *  2. GEOCENTRIC, not topocentric. Lunar parallax reaches ~1 degree, which is
 *     enough to place the Moon in a different nakshatra. Swiss Ephemeris and
 *     standard Indian panchanga software default to geocentric; we match.
 *  3. APPARENT positions — light-time corrected and aberrated, matching the
 *     Swiss Ephemeris default.
 *  4. Rahu is the MEAN lunar node (Ketu exactly opposite). The true node
 *     differs by up to ~1.7 degrees. Mean is the panchanga norm.
 */

export const GRAHAS = [
  'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu',
] as const;

export type Graha = (typeof GRAHAS)[number];

const BODY_FOR: Partial<Record<Graha, Astronomy.Body>> = {
  Sun: Astronomy.Body.Sun,
  Mars: Astronomy.Body.Mars,
  Mercury: Astronomy.Body.Mercury,
  Jupiter: Astronomy.Body.Jupiter,
  Venus: Astronomy.Body.Venus,
  Saturn: Astronomy.Body.Saturn,
};

export interface GeoPoint {
  /** Degrees north; negative south. */
  latitude: number;
  /** Degrees east; negative west. */
  longitude: number;
}

export interface Positions {
  /** Sidereal ecliptic longitude in degrees [0, 360) per graha. */
  longitudes: Record<Graha, number>;
  /** True when the graha's apparent motion is retrograde. */
  retrograde: Record<Graha, boolean>;
  /** Sidereal Ascendant (Lagna) in degrees, when a location was supplied. */
  ascendant?: number;
  /** Ayanamsa applied, in degrees — surfaced in the UI for transparency. */
  ayanamsa: number;
}

/** Tropical apparent ecliptic-of-date longitude of a body, in degrees. */
function tropicalLongitude(body: Astronomy.Body, time: Astronomy.AstroTime): number {
  const vec = Astronomy.GeoVector(body, time, true); // aberration + light-time
  return normalize360(Astronomy.Ecliptic(vec).elon);
}

/**
 * Mean longitude of the ascending lunar node (Rahu), in degrees.
 *
 * Meeus, Astronomical Algorithms 2nd ed., eq. 47.7. This is a definition
 * rather than an approximation, and is short enough to carry safely by hand.
 * T is Julian centuries of TT from J2000.
 */
export function meanNodeLongitude(time: Astronomy.AstroTime): number {
  const T = time.tt / 36525;
  const omega =
    125.0445479 -
    1934.1362891 * T +
    0.0020754 * T * T +
    (T * T * T) / 467441 -
    (T * T * T * T) / 60616000;
  return normalize360(omega);
}

/**
 * Ascendant (Lagna): the ecliptic degree rising on the eastern horizon.
 *
 * The atan2 form below is the classic home of silent 180-degree and quadrant
 * errors, so `verifyAscendant` re-derives the result by an independent method
 * and callers must not ship a chart when that check fails.
 *
 * theta = local apparent sidereal time, eps = true obliquity, phi = latitude.
 */
export function tropicalAscendant(time: Astronomy.AstroTime, place: GeoPoint): number {
  const gastHours = Astronomy.SiderealTime(time);
  const lstDeg = normalize360(gastHours * 15 + place.longitude);
  const theta = (lstDeg * Math.PI) / 180;
  const eps = (trueObliquityDeg(time) * Math.PI) / 180;
  const phi = (place.latitude * Math.PI) / 180;

  const asc = Math.atan2(
    Math.cos(theta),
    -(Math.sin(theta) * Math.cos(eps) + Math.tan(phi) * Math.sin(eps)),
  );
  return normalize360((asc * 180) / Math.PI);
}

/**
 * True obliquity of the ecliptic in degrees, recovered from the library's own
 * ECT rotation rather than a hand-written nutation series — the ECT frame is
 * defined by the true obliquity, so the matrix already carries it.
 */
export function trueObliquityDeg(time: Astronomy.AstroTime): number {
  // Rotate the ecliptic pole (0,0,1 in ECT) into equator-of-date coordinates;
  // its angle from the equatorial pole is the obliquity.
  const rot = Astronomy.Rotation_ECT_EQD(time);
  const pole = Astronomy.RotateVector(rot, new Astronomy.Vector(0, 0, 1, time));
  return (Math.acos(pole.z) * 180) / Math.PI;
}

/**
 * Independent verification of the Ascendant.
 *
 * Takes the computed ecliptic point, rotates it to horizontal coordinates for
 * the observer, and checks it really is on the horizon (altitude ~ 0) and in
 * the eastern half of the sky (azimuth between north and south through east).
 * Any sign or quadrant slip in `tropicalAscendant` fails this immediately.
 *
 * Returns the altitude error in degrees, or null if the check cannot run.
 */
export function verifyAscendant(
  time: Astronomy.AstroTime,
  place: GeoPoint,
  ascendantDeg: number,
): { ok: boolean; altitude: number; azimuth: number } {
  const lonRad = (ascendantDeg * Math.PI) / 180;
  const ect = new Astronomy.Vector(Math.cos(lonRad), Math.sin(lonRad), 0, time);
  const eqd = Astronomy.RotateVector(Astronomy.Rotation_ECT_EQD(time), ect);
  const sphere = Astronomy.SphereFromVector(eqd);

  const observer = new Astronomy.Observer(place.latitude, place.longitude, 0);
  const raHours = normalize360(sphere.lon) / 15;
  const decDeg = sphere.lat;
  /* Refraction must be OFF. The Ascendant is a geometric intersection of the
     ecliptic with the horizon plane, not an observed rising position — asking
     for refracted altitude introduces the ~0.5 degree horizon refraction and
     makes a correct result look wrong. */
  const hor = Astronomy.Horizon(time, observer, raHours, decDeg);

  // Rising point: on the horizon, and east of the meridian.
  const ok = Math.abs(hor.altitude) < 0.02 && hor.azimuth > 0 && hor.azimuth < 180;
  return { ok, altitude: hor.altitude, azimuth: hor.azimuth };
}

/** Is the body's apparent ecliptic longitude decreasing? */
function isRetrograde(body: Astronomy.Body, time: Astronomy.AstroTime): boolean {
  const dt = 0.5; // days
  const before = tropicalLongitude(body, Astronomy.MakeTime(time.tt - dt));
  const after = tropicalLongitude(body, Astronomy.MakeTime(time.tt + dt));
  let delta = after - before;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta < 0;
}

/**
 * All nine grahas plus (optionally) the Ascendant, as sidereal longitudes.
 *
 * @param time  the instant, as an AstroTime
 * @param place omit to skip the Ascendant — it is meaningless without a location
 */
export function positionsAt(time: Astronomy.AstroTime, place?: GeoPoint): Positions {
  const ayan = ayanamsaDeg(time);
  const sidereal = (tropical: number) => normalize360(tropical - ayan);

  const longitudes = {} as Record<Graha, number>;
  const retrograde = {} as Record<Graha, boolean>;

  for (const graha of GRAHAS) {
    if (graha === 'Moon') {
      longitudes.Moon = sidereal(normalize360(Astronomy.EclipticGeoMoon(time).lon));
      retrograde.Moon = false; // the Moon is never retrograde in geocentric terms
      continue;
    }
    if (graha === 'Rahu' || graha === 'Ketu') {
      const node = meanNodeLongitude(time);
      longitudes[graha] = sidereal(graha === 'Rahu' ? node : normalize360(node + 180));
      // The mean nodes always regress along the ecliptic.
      retrograde[graha] = true;
      continue;
    }
    const body = BODY_FOR[graha]!;
    longitudes[graha] = sidereal(tropicalLongitude(body, time));
    retrograde[graha] = graha === 'Sun' ? false : isRetrograde(body, time);
  }

  const out: Positions = { longitudes, retrograde, ayanamsa: ayan };

  if (place) {
    out.ascendant = sidereal(tropicalAscendant(time, place));
  }

  return out;
}

/** Convenience entry point for callers holding a JS Date (must be UTC). */
export function positionsAtDate(utc: Date, place?: GeoPoint): Positions {
  return positionsAt(Astronomy.MakeTime(utc), place);
}

export { timeFromJD };
